// Monthly usage per workshop, the unit Tallerza bills by (docs/comercial/
// lanzamiento.md): "a car is each vehicle (plate) with at least one
// inspection in the month", counted once per month. onInspeccionFirmada keeps
// `talleres/{t}/uso/{AAAA-MM}` (field `autos`) with one document per plate in
// `talleres/{t}/uso/{AAAA-MM}/patentes/{patente}`, so a retried trigger or a
// second inspection of the same car never counts twice.
import { diaChile } from './metricas.ts';
import { normalizarPatente, patente as esquemaPatente } from './modelo.ts';

/** `talleres/{t}/uso/{AAAA-MM}`, as written by the Functions. */
export type UsoMes = {
  mes: string;
  autos: number;
};

/** 'AAAA-MM' of an instant in Chile (America/Santiago, DST aware). */
export function mesChile(ms: number): string {
  return diaChile(ms).slice(0, 7);
}

const MES = /^(\d{4})-(0[1-9]|1[0-2])$/;

export const esMes = (m: string) => MES.test(m);

/** The month before 'AAAA-MM' ('2026-01' → '2025-12'). */
export function mesAnterior(mes: string): string {
  const r = MES.exec(mes);
  if (!r) throw new Error(`Mes inválido: ${mes}`);
  const [a, m] = [Number(r[1]), Number(r[2])];
  return m === 1 ? `${a - 1}-12` : `${a}-${String(m - 1).padStart(2, '0')}`;
}

/**
 * The plate as the usage document id: upper case, letters and digits only
 * (the same rule as `vehiculos/{patente}`). Null when it is not a valid plate,
 * so a malformed value can never become a path segment.
 */
export function patenteDeUso(p: unknown): string | null {
  if (typeof p !== 'string') return null;
  const n = normalizarPatente(p);
  return esquemaPatente.safeParse(n).success ? n : null;
}

/**
 * Launch plans by cars per month (CLP + IVA, docs/comercial/lanzamiento.md).
 * `hasta` is the plan's cap; for Por uso it is where it stops being the
 * cheapest one: 50 × $990 = $49.500 < $49.990 (Taller) < 51 × $990.
 */
export const PRECIO_POR_AUTO = 990;
export const PLANES_USO = [
  { id: 'tallerza_uso', nombre: 'Por uso', hasta: 50 },
  { id: 'tallerza_base', nombre: 'Taller', hasta: 60 },
  { id: 'tallerza_pro', nombre: 'Taller Pro', hasta: 150 },
  { id: 'tallerza_volumen', nombre: 'Alto volumen', hasta: 250 },
] as const;

export type PlanUso = (typeof PLANES_USO)[number];

/**
 * Suggested plan for a month's cars: the cheapest one whose cap covers them.
 * Over the largest cap it stays at Alto volumen with `sobreTope` (per car or
 * a contract, never blocked).
 */
export function planSugerido(autos: number): { plan: PlanUso; sobreTope: boolean } {
  const n = Math.max(0, Math.floor(autos));
  const plan = PLANES_USO.find((p) => n <= p.hasta);
  return plan ? { plan, sobreTope: false } : { plan: PLANES_USO[3], sobreTope: true };
}
